import { AuthMethod, type CreatedUser, type MfaReset, type OrganizationDetail, type OrganizationSummary, type PortalGroup, type User, type UserDetail, type UserUpdate } from '@/portalApi'
import useAuthStore from '@/stores/auth'
import { USERS_QUERY_KEY, usersQuery } from '@/stores/users.store'
import { portalApi } from '@/util/apiClient'
import { queryClient } from '@/util/queryClient'
import type { RequiredRole } from '@/util/roles/generated'
import Toasts from '@/util/toasts'
import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'

// Galvanick's own organization, whose members administer every other.
const INTERNAL_ORG_NAME = 'galvanick'

const isInternal = (org: OrganizationSummary): boolean =>
  org.name?.toLowerCase() === INTERNAL_ORG_NAME

/**
 * How long the administration reads are held. Long enough that opening the users
 * tab, leaving it and opening it again does not read everything three times;
 * short enough that a change another administrator made shows up without a
 * reload. A change made here does not wait for it — every mutation invalidates.
 */
const USERS_STALE_MS = 5 * 60 * 1000

/** Under one prefix, so invalidating the lot is a single call. */
const USERS_KEYS = {
  all: ['userAdmin'],
  organizations: ['userAdmin', 'organizations'],
  organization: (id: string) => ['userAdmin', 'organization', id],
  groups: ['userAdmin', 'groups'],
}

/**
 * One read, served from the cache when it is already held and shared when the
 * same read is already in flight — which is what stops the view and its tab
 * asking for the same thing twice as they mount together.
 */
const cachedRead = <T>(queryKey: unknown[], queryFn: () => Promise<T>) =>
  queryClient.query({ queryKey, queryFn, staleTime: USERS_STALE_MS })

// A role as it is offered to the user: the value to assign, and how to describe it.
export type RoleMeta = {
  value: RequiredRole
  label: string
  description: string
}

// Role metadata for display, in descending order of privilege
export const ROLE_META: RoleMeta[] = [
  {
    value: 'galvanick:admin',
    label: 'Admin',
    description:
      'Full read/write access. Manages users, roles, appliances and network configuration.',
  },
  {
    value: 'galvanick:user',
    label: 'User',
    description:
      'Investigates and triages findings, and generates reports. Cannot update admin settings or users.',
  },
  {
    value: 'galvanick:read-only',
    label: 'Read-only',
    description:
      'Read access only. Views dashboards, findings, and assets. Cannot modify any data.',
  },
]

// Organizations with their users, and the group assignments that grant each role
export const useUserRolesStore = defineStore('userRoles', () => {
  const organizations: Ref<OrganizationDetail[]> = ref([])
  const orphanedUsers: Ref<UserDetail[]> = ref([])
  const groups: Ref<PortalGroup[]> = ref([])
  const loading = ref(false)
  // Id of the user whose role is mid-save, so their row can disable its select.
  const saving = ref<string | undefined>(undefined)
  // Id of the user whose password is mid-reset, so the dialog can disable its
  // confirm rather than issue a second reset that invalidates the first password.
  const resettingPassword = ref<string | undefined>(undefined)
  // Id of the user whose MFA is mid-reset, so the dialog can disable its confirm.
  const resettingMfa = ref<string | undefined>(undefined)
  // Whether a create is in flight, so the form can disable its submit rather than
  // create the same person twice.
  const creatingUser = ref(false)
  // Id of the user whose profile is mid-save, so the form can disable its submit.
  const updatingUser = ref<string | undefined>(undefined)
  // Id of the user whose account state is mid-save, so the confirmation can
  // disable its confirm rather than send the same change twice.
  const settingEnabled = ref<string | undefined>(undefined)

  // The organization names on the access token's claim.
  const claimedOrgNames = computed(
    () =>
      new Set(
        useAuthStore().organizations.map((claimed) => claimed.toLowerCase()),
      ),
  )

  /**
   * Whether this organization is the caller's own. Check membership first, then
   * fall back to the access token "organization" claim.
   */
  const isCallerOrganization = (org: OrganizationDetail): boolean => {
    const authStore = useAuthStore()
    if ((org.users ?? []).some((user) => authStore.isCurrentUser(user.id))) {
      return true
    }
    return [org.alias, org.name].some(
      (value) => !!value && claimedOrgNames.value.has(value.toLowerCase()),
    )
  }

  // Galvanick's own staff, who administer every organization rather than one.
  const isInternalCaller = computed(() =>
    organizations.value.some(
      (org) => isInternal(org) && isCallerOrganization(org),
    ),
  )

  /**
   * The organization that leads the list: the caller's own, so the people they
   * administer are the first table they see. Galvanick's own is excluded — staff
   * administer every organization, so theirs belongs at the end rather than ahead
   * of the customers they are looking at.
   */
  const leadsList = (org: OrganizationDetail): boolean =>
    !isInternal(org) && isCallerOrganization(org)

  // Organizations in display order: the caller's own first, then the rest by name,
  // with the Galvanick org always last. Each organization is classified once,
  // rather than re-scanning its users on every comparison the sort makes.
  const orderedOrganizations = computed(() =>
    organizations.value
      .map((org) => ({
        org,
        own: leadsList(org),
        internal: isInternal(org),
        name: org.name ?? '',
      }))
      .sort((a, b) => {
        if (a.own !== b.own) return a.own ? -1 : 1
        if (a.internal !== b.internal) return a.internal ? 1 : -1
        return a.name.localeCompare(b.name)
      })
      .map(({ org }) => org),
  )

  // Groups keyed by the role they grant
  const groupByRole = computed(() => {
    const map = new Map<string, PortalGroup>()
    for (const group of groups.value) {
      if (group.role && !map.has(group.role)) map.set(group.role, group)
    }
    return map
  })

  // Roles that can actually be assigned, in case others were added to Keycloak
  const assignableRoles = computed(() =>
    ROLE_META.filter((role) => groupByRole.value.has(role.value)),
  )

  // Whether a user's role can be updated by the current caller
  const isManageable = (user: UserDetail): boolean => user.manageable === true

  // Whether we manage passwords for this user (true), or they use an IdP (false)
  const hasPassword = (user: UserDetail): boolean =>
    user.auth_method === AuthMethod.password

  // Whether this organization brokers logins to an external identity provider
  const usesIdp = (org: OrganizationSummary): boolean =>
    org.auth_method === AuthMethod.idp

  // Can a user role be updated. If the user has a role granted through
  // an internal group (i.e., Galvanick Users), this will return false.
  // The API will reject removing any internal group (assignable = false).
  const isRoleEditable = (user: UserDetail): boolean =>
    isManageable(user) &&
    (user.groups ?? []).every((group) => group.assignable !== false)

  // The single role to show for a user.  It's possible they may have multiple,
  // if assigned in Keycloak, but we attempt to enforce one role per user client-side.
  // If multiple, show the role with highest privilege, defined by order of ROLE_META,
  // since this is how backend enforcement behaves.
  const roleOf = (user: UserDetail): RequiredRole | undefined =>
    ROLE_META.find((role) => user.roles?.includes(role.value))?.value

  // Organizations with nobody holding the admin role. A disabled administrator
  // cannot sign in, so they do not count as one.
  const organizationsWithoutAdmin = computed(() => {
    if (loading.value) return []
    return orderedOrganizations.value.filter(
      (org) =>
        !isInternal(org) &&
        (isInternalCaller.value || isCallerOrganization(org)) &&
        !(org.users ?? []).some(
          (user) =>
            roleOf(user) === 'galvanick:admin' && user.enabled !== false,
        ),
    )
  })

  /**
   * Everyone held here, whichever organization holds them, and those held by
   * none — the set the tables list. Where users live is this store's business, so
   * a caller wanting all of them asks rather than walking the organizations.
   */
  const listedUserIds = computed(() =>
    [
      ...organizations.value.flatMap((org) =>
        (org.users ?? []).map((user) => user.id),
      ),
      ...orphanedUsers.value.map((user) => user.id),
    ].filter((id): id is string => !!id),
  )

  // Users live under their organization, so a lookup by id spans all of them,
  // and then the users belonging to none.
  const userById = (userId: string): UserDetail | undefined => {
    for (const org of organizations.value) {
      const found = (org.users ?? []).find((u) => u.id === userId)
      if (found) return found
    }
    return orphanedUsers.value.find((u) => u.id === userId)
  }

  /**
   * Read every organization then its users. The admin view and its users tab
   * both ask — the view for its no-administrator banner, the tab for its tables
   * — and the tab asks again every time it is opened, so the reads are cached:
   * the second caller, and a return to the tab, are served what the first read
   * rather than repeating all of it.
   */
  const fetchAll = async () => {
    if (loading.value) return
    loading.value = true
    try {
      const [summaries, portalGroups, realmUsers] = await Promise.all([
        cachedRead(USERS_KEYS.organizations, () =>
          portalApi.usersOrganizationsGet(),
        ),
        cachedRead(USERS_KEYS.groups, () => portalApi.usersGroupsGet()),
        queryClient.query(usersQuery()),
      ])
      groups.value = portalGroups
      organizations.value = await Promise.all(
        summaries
          .filter((summary) => !!summary.id)
          .map((summary) =>
            cachedRead(USERS_KEYS.organization(summary.id!), () =>
              portalApi.usersOrganizationsIdGet({ path: { id: summary.id! } }),
            ),
          ),
      )
      await fetchOrphaned(realmUsers)
    } catch (err) {
      console.error(err)
      Toasts.Error('Error loading users', 'See console for details')
    } finally {
      loading.value = false
    }
  }

  /**
   * Drops those cached reads. Every mutation here ends with one, since the next
   * `fetchAll` rebuilds the organizations from whatever the cache holds — and
   * would otherwise put back the roster from before the change.
   *
   * The shared users list goes with them, so the assignee dropdown and the names
   * shown elsewhere follow the change rather than keeping what they first read.
   */
  const invalidateUsers = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: USERS_KEYS.all }),
      queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY }),
    ])

  // Figure out which users are not part of an organization.
  const fetchOrphaned = async (realmUsers: User[]) => {
    const claimed = new Set(
      organizations.value.flatMap((org) =>
        (org.users ?? []).map((user) => user.id),
      ),
    )
    const unclaimed = realmUsers
      .map((user) => user.id)
      .filter((id): id is string => !!id && !claimed.has(id))
    if (!unclaimed.length) {
      orphanedUsers.value = []
      return
    }
    // Fetch details for any orphaned users. This will likely be
    // a very small number of users, if any, so not too worried
    // about fanning out these requests.
    const results = await Promise.allSettled(
      unclaimed.map((id) => portalApi.usersIdGet({ path: { id } })),
    )
    results
      .filter((result) => result.status === 'rejected')
      .forEach((result) => console.error(result.reason))
    orphanedUsers.value = results
      .filter(
        (result): result is PromiseFulfilledResult<UserDetail> =>
          result.status === 'fulfilled',
      )
      .map((result) => result.value)
  }

  // Put a user in exactly the group granting `role`, removing every other group
  // after add to enforce one role per user.

  /**
   * Replaces one user wherever they are held: under their organization, or in
   * the orphaned list for a user belonging to none.
   */
  const mergeUser = (updated: UserDetail) => {
    organizations.value = organizations.value.map((org) => ({
      ...org,
      users: (org.users ?? []).map((u) => (u.id === updated.id ? updated : u)),
    }))
    orphanedUsers.value = orphanedUsers.value.map((u) =>
      u.id === updated.id ? updated : u,
    )
  }

  /**
   * Re-reads one user and patches them into whichever organization holds them,
   * so callers never need to know which that is.
   */
  const refreshUser = async (userId: string) => {
    mergeUser(await portalApi.usersIdGet({ path: { id: userId } }))
  }

  /**
   * Resolves with `confirmed: false` when the change landed but could not be
   * read back, so the caller can say "changed, but not confirmed" rather than
   * reporting a completed privilege change as a failure.
   */
  const setRole = async (
    userId: string,
    role: RequiredRole,
  ): Promise<{ confirmed: boolean }> => {
    const target = groupByRole.value.get(role)
    if (!target?.id) {
      throw new Error(`No portal group grants the role ${role}`)
    }
    const user = userById(userId)
    // The API answers a mutation against an unmanageable user with a 404, which
    // reads as "user does not exist". Refuse up front so the caller gets the
    // real reason instead.
    if (user && !isManageable(user)) {
      throw new Error(
        'This user belongs to another organization and cannot be changed here',
      )
    }
    // Refused here rather than attempted, because the group operations answer a
    // group the portal does not manage with a 400, and by then the add below has
    // already landed and left the user holding two roles.
    if (user && !isRoleEditable(user)) {
      throw new Error(
        'This user holds a group the portal does not manage, so their role is set outside it',
      )
    }
    // A group the portal does not manage cannot be removed, so asking would only
    // fail after the add had already landed.
    const stale = (user?.groups ?? []).filter(
      (group) =>
        group.id && group.id !== target.id && group.assignable !== false,
    )

    saving.value = userId
    try {
      await portalApi.usersGroupPut({ path: { id: userId, group_id: target.id } })
      // Their groups have changed, so the rosters held for the tables have not.
      invalidateUsers()
      try {
        for (const group of stale) {
          await portalApi.usersGroupDelete({ path: { id: userId, group_id: group.id! } })
        }
      } catch (err) {
        // The add succeeded, so the user now holds both roles. Re-read before
        // rethrowing: leaving the row showing the role they had before would
        // hide the extra access they actually have.
        await refreshUser(userId).catch((readErr) => console.error(readErr))
        throw err
      }
      // Past this point the change has landed. A failed read-back is a stale
      // view, not a failed change, so it must not be reported as one.
      try {
        await refreshUser(userId)
      } catch (err) {
        console.error(err)
        return { confirmed: false }
      }
      return { confirmed: true }
    } finally {
      saving.value = undefined
    }
  }

  /**
   * Creates a user in one organization, in the group granting `role`, and returns
   * them with the temporary password to relay.
   */
  const createUser = async (newUser: {
    organizationId: string
    given_name: string
    family_name: string
    email: string
    role: RequiredRole
  }): Promise<CreatedUser> => {
    const group = groupByRole.value.get(newUser.role)
    if (!group?.id) {
      throw new Error(`No portal group grants the role ${newUser.role}`)
    }
    const org = organizations.value.find((o) => o.id === newUser.organizationId)
    if (org && org.manageable !== true) {
      throw new Error(
        'You cannot add users to an organization you do not administer',
      )
    }
    if (org && usesIdp(org)) {
      throw new Error(
        'This organization brokers logins to an identity provider, which owns its users',
      )
    }

    creatingUser.value = true
    try {
      const created = await portalApi.usersPost({
        body: {
          email: newUser.email,
          given_name: newUser.given_name,
          family_name: newUser.family_name,
          organization_id: newUser.organizationId,
          group_id: group.id,
        }
      })
      // The usersPost response is the same as GET /users/{id}, so they can be
      // added to their organization's table without re-reading every one, but
      // drop the temporary password first.
      const { temporary_password: _password, ...user } = created
      organizations.value = organizations.value.map((o) =>
        o.id === newUser.organizationId
          ? { ...o, users: [...(o.users ?? []), user] }
          : o,
      )
      // The held roster does not have them in it, so it is no longer the roster.
      invalidateUsers()
      return created
    } finally {
      creatingUser.value = false
    }
  }

  /**
   * Changes a user's name or email. Only the fields that differ are sent, and a
   * form saved without touching anything makes no request at all, resolving with
   * undefined so the caller can tell that nothing changed.
   */
  const updateUser = async (
    userId: string,
    profile: { given_name: string; family_name: string; email: string },
  ): Promise<UserDetail | undefined> => {
    const user = userById(userId)
    if (!user) {
      throw new Error('This user is no longer listed')
    }
    // The API answers a mutation against an unmanageable user with a 404, which
    // reads as "user does not exist". Refuse up front so the caller gets the
    // real reason instead.
    if (!isManageable(user)) {
      throw new Error(
        'This user belongs to another organization and cannot be changed here',
      )
    }

    const changes: UserUpdate = {}
    if (profile.given_name !== user.given_name) {
      changes.given_name = profile.given_name
    }
    if (profile.family_name !== user.family_name) {
      changes.family_name = profile.family_name
    }
    // The email is also the sign-in name, which an identity provider owns for the
    // users it brokers. Asking anyway is answered with a 409, the same status as a
    // taken address, so the two are never confused.
    if (profile.email !== user.email) {
      if (!hasPassword(user)) {
        throw new Error(
          'This user signs in through an identity provider, which holds their email address',
        )
      }
      changes.email = profile.email
    }
    if (!Object.keys(changes).length) return undefined

    updatingUser.value = userId
    try {
      const updated = await portalApi.usersIdPatch({ path: { id: userId }, body: changes })
      // The response is the user as a read would report them, including the
      // lowercased form the email was stored as.
      mergeUser(updated)
      // The held roster still has their old name or email on it.
      invalidateUsers()
      return updated
    } finally {
      updatingUser.value = undefined
    }
  }

  /**
   * Turns a user's portal access on or off. This is how access is taken away —
   * there is no delete — so everything attributed to them stays attributed.
   */
  const setEnabled = async (
    userId: string,
    enabled: boolean,
  ): Promise<UserDetail> => {
    const user = userById(userId)
    // The API answers a mutation against an unmanageable user with a 404, which
    // reads as "user does not exist". Refuse up front so the caller gets the
    // real reason instead.
    if (user && !isManageable(user)) {
      throw new Error(
        'This user belongs to another organization and cannot be changed here',
      )
    }
    // Refused with a 403 by the API in either direction. Refuse here too, so an
    // administrator cannot take away their own access by any route.
    if (useAuthStore().isCurrentUser(userId)) {
      throw new Error('You cannot change your own account state')
    }

    settingEnabled.value = userId
    try {
      const updated = await portalApi.usersEnabledPut({ path: { id: userId }, body: { enabled } })
      // The response is the user as a read would report them.
      mergeUser(updated)
      // The held roster still says they have access.
      invalidateUsers()
      return updated
    } finally {
      settingEnabled.value = undefined
    }
  }

  // Replaces a user's password with a generated temporary one and returns it.
  const resetPassword = async (userId: string): Promise<string> => {
    const user = userById(userId)
    if (user && !isManageable(user)) {
      throw new Error(
        'This user belongs to another organization and cannot be changed here',
      )
    }
    if (user && !hasPassword(user)) {
      throw new Error(
        'This user signs in through an identity provider, which holds their password',
      )
    }

    resettingPassword.value = userId
    try {
      const { temporary_password } = await portalApi.usersPasswordPost({ path: { id: userId } })
      // Refresh user state so we pick up the must_change_password=true flag
      invalidateUsers()
      await refreshUser(userId).catch((err) => console.error(err))
      return temporary_password
    } finally {
      resettingPassword.value = undefined
    }
  }

  // Removes every second factor a user holds. Their password is kept.
  const resetMfa = async (userId: string): Promise<MfaReset> => {
    const user = userById(userId)
    if (user && !isManageable(user)) {
      throw new Error(
        'This user belongs to another organization and cannot be changed here',
      )
    }
    if (user?.auth_method === AuthMethod.idp) {
      throw new Error(
        'This user signs in through an identity provider, which holds their MFA',
      )
    }

    resettingMfa.value = userId
    try {
      return await portalApi.usersIdMfaDelete({ path: { id: userId } })
    } finally {
      resettingMfa.value = undefined
    }
  }

  return {
    assignableRoles,
    createUser,
    creatingUser,
    fetchAll,
    groupByRole,
    groups,
    isInternalOrg: isInternal,
    isManageable,
    invalidateUsers,
    isRoleEditable,
    listedUserIds,
    loading,
    orderedOrganizations,
    organizations,
    organizationsWithoutAdmin,
    orphanedUsers,
    resetMfa,
    resetPassword,
    resettingMfa,
    resettingPassword,
    roleOf,
    saving,
    setEnabled,
    setRole,
    settingEnabled,
    updateUser,
    updatingUser,
    userById,
    usesIdp,
  }
})

export default useUserRolesStore
